/**
 * Cliente del repaso de correcciones con el coach (charla asistida, encargo 99):
 *   - `checkCorrectionReview`: lo que el alumno dijo al repasar una corrección
 *     (WAV) → transcripción sin contexto + la vara de las correcciones.
 *   - `fetchDueCorrections`: el Repaso del día (las que ya tocan, con tope).
 *   - `fetchReviewCues`: la pista en español del «sin mirar» y la regla del error.
 * Un no-2xx TIRA: la pantalla dice que no se pudo, nunca inventa un resultado.
 */
import type { CorrectionReviewMatch } from '@orborix/shared-types';
import { apiFetch } from './apiClient.js';

const BASE = '/api/modules/english';

export interface CorrectionReviewCheck {
  heard: string;
  match: CorrectionReviewMatch;
}

export async function checkCorrectionReview(
  id: string,
  audio: Blob,
): Promise<CorrectionReviewCheck> {
  const fd = new FormData();
  fd.append('id', id);
  fd.append('audio', audio, 'review.wav');
  const res = await apiFetch(`${BASE}/corrections/review-check`, { method: 'POST', body: fd });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  const json = (await res.json()) as { data?: CorrectionReviewCheck };
  if (!json.data || typeof json.data.heard !== 'string' || !json.data.match) {
    throw new Error('review-check: respuesta inválida');
  }
  return json.data;
}

/** Una corrección que ya toca repasar (agrupada por frase). */
export interface DueCorrection {
  /**
   * 'phrase' = una frase de la charla asistida que siguió sin intentarla
   * (Emmanuel, 19/10): sin `said` ni explicación, con su traducción. 'word' = una
   * palabra guardada a mano (Emmanuel, 20/10), dentro de su frase de ejemplo; van
   * después de las correcciones, como mucho 2 por día.
   */
  kind: 'correction' | 'phrase' | 'word';
  id: string;
  memberIds: string[];
  /** `null` en una palabra guardada (no sale de una charla). */
  sessionId: string | null;
  turnIndex: number | null;
  said: string;
  corrected: string;
  explanation: string | null;
  /** Lo que el coach dijo justo antes del error; `null` si no hay. */
  coachBefore: string | null;
  nextReviewAt: string;
  /** Ya se repasó antes (vuelve): el repaso arranca sin mirar. */
  reviewed: boolean;
  /** Sólo `kind: 'phrase'`: su traducción. */
  translation?: string | null;
  /** Sólo `kind: 'word'`: la palabra guardada. */
  word?: string;
  /** Es ejemplo de un foco activo del Radar (va primero, «Es de tu radar»). */
  radar?: { focusId: string; label: string };
}

export interface DueCorrections {
  /** Las que entran hoy (ya descontado el tope). */
  items: DueCorrection[];
  /** Todas las vencidas (agrupadas), entren o no hoy. */
  dueCount: number;
  reviewedToday: number;
  cap: number;
}

/**
 * Con `ahead`, las de MAÑANA (lo de hoy ya guardado): el final del repaso dice
 * «mañana te tocan N» con `items.length` (card #604).
 */
export async function fetchDueCorrections(
  signal?: AbortSignal,
  opts: { ahead?: boolean } = {},
): Promise<DueCorrections> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const q = `tz=${encodeURIComponent(tz)}${opts.ahead ? '&ahead=1' : ''}`;
  const res = await apiFetch(`${BASE}/corrections/due?${q}`, {
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) throw new Error(`corrections/due: HTTP ${res.status}`);
  const json = (await res.json()) as { data?: DueCorrections };
  if (!json.data || !Array.isArray(json.data.items))
    throw new Error('corrections/due: respuesta inválida');
  return json.data;
}

/** Una frase de un foco del Radar (agrupada por frase), con cómo va en el repaso. */
export interface FocusCorrection {
  id: string;
  memberIds: string[];
  said: string;
  corrected: string;
  explanation: string | null;
  reviewed: boolean;
  /** La última vuelta fue de memoria. */
  fromMemory: boolean;
  /** Cuándo vuelve; `null` = nunca repasada o aprendida. */
  nextReviewAt: string | null;
}

export interface FocusCorrections {
  items: FocusCorrection[];
  /** Cuántas de `items` se repasaron de memoria. */
  fromMemory: number;
  /** La próxima que vuelve de las repasadas de memoria; `null` = ninguna. */
  nextReviewAt: string | null;
}

/** Las frases de un foco del Radar para su ficha (Emmanuel, 19/10). Sólo lee el foco. */
export async function fetchFocusCorrections(
  focusId: string,
  signal?: AbortSignal,
): Promise<FocusCorrections> {
  const res = await apiFetch(`${BASE}/corrections/by-focus/${encodeURIComponent(focusId)}`, {
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) throw new Error(`corrections/by-focus: HTTP ${res.status}`);
  const json = (await res.json()) as { data?: FocusCorrections };
  if (!json.data || !Array.isArray(json.data.items) || typeof json.data.fromMemory !== 'number')
    throw new Error('corrections/by-focus: respuesta inválida');
  return json.data;
}

/** La pista en español del «sin mirar» y la regla del error, por id de corrección. */
export interface ReviewCues {
  /** Qué decir sin mirar, en español; `null` = sin pista (va la traducción). */
  cues: Record<string, string | null>;
  /** La regla del error, sin la respuesta (sin mirar, tras un error); `null` = sin regla. */
  hints: Record<string, string | null>;
  /**
   * Lo que se repasa de cada corrección: la de una palabra, con la oración entera
   * del alumno corregida (Emmanuel, 20/10: «cook → cooks» se repasa como «My
   * brother cooks the dinner.»). Las frases de la charla asistida no vienen.
   */
  forms: Record<string, { said: string; corrected: string }>;
  /**
   * Las correcciones sin ningún pedazo de su oración que se diga solo (card #580): no se
   * repasan. El server ya las sacó del repaso; la tarjeta se saltea.
   */
  skip: ReadonlyArray<string>;
}

/**
 * La pista del «sin mirar» y la regla del error, por id de corrección (hasta
 * 10). Las escribe el server una vez y las guarda. Sin pregunta del coach: el
 * repaso va rápido, sin que el coach hable en cada tarjeta (Emmanuel, 16/10).
 */
export function fetchReviewCues(ids: ReadonlyArray<string>): Promise<ReviewCues> {
  // El Inicio las pide de antemano y el repaso de nuevo al abrir: el mismo
  // pedido se comparte (el server no las escribe ni las cobra dos veces). Un
  // fallo no queda guardado: el próximo vuelve a intentar.
  const key = [...ids].sort().join(',');
  const known = cueRequests.get(key);
  if (known) return known;
  const request = requestReviewCues(ids).catch((err: unknown) => {
    cueRequests.delete(key);
    throw err;
  });
  cueRequests.set(key, request);
  return request;
}

const cueRequests = new Map<string, Promise<ReviewCues>>();

async function requestReviewCues(ids: ReadonlyArray<string>): Promise<ReviewCues> {
  const res = await apiFetch(`${BASE}/corrections/review-cues`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`corrections/review-cues: HTTP ${res.status}`);
  const json = (await res.json()) as { data?: Partial<ReviewCues> };
  const { cues, hints, forms, skip } = json.data ?? {};
  if (!cues || !hints || !forms || !skip) {
    throw new Error('corrections/review-cues: respuesta inválida');
  }
  return { cues, hints, forms, skip };
}
